import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AmbulanceConflicts } from "@/components/AmbulanceConflicts";
import { volunteerQuals } from "@/lib/quals";
import { DuplicateWarning } from "@/components/DuplicateWarning";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";

const EMPTY = {
  event_name: "", date_time: "", location: "", service_type: "",
  required_ambulances: 1, required_volunteers: 2, required_medici: 0, required_infermieri: 0, whatsapp_link: "", notes: "",
};

export function ServiceFormDialog({ open, onOpenChange, service, template, onSaved }) {
  const { settings } = useSettings();
  const isEdit = !!service;
  const src = service || template;
  const [form, setForm] = useState(EMPTY);
  const [quals, setQuals] = useState([]);
  const [dates, setDates] = useState([""]);
  const [ends, setEnds] = useState([""]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [ambIds, setAmbIds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (src) {
        setForm({
          event_name: src.event_name, date_time: service ? src.date_time : "", location: src.location,
          service_type: src.service_type, required_ambulances: src.required_ambulances,
          required_volunteers: src.required_volunteers, notes: src.notes || "",
          required_medici: src.required_medici || 0, required_infermieri: src.required_infermieri || 0,
          whatsapp_link: service ? src.whatsapp_link || "" : "",
        });
        setQuals(volunteerQuals(src.required_qualifications));
        const svcDates = src.dates?.length ? src.dates : [src.date_time || ""];
        setDates(service ? svcDates : [""]);
        setEnds(service ? svcDates.map((d) => src.end_times?.[d] || "") : [src.end_times?.[svcDates[0]] || ""]);
        setVehicleIds((src.required_vehicles || []).map((v) => v.id));
        setAmbIds((src.ambulances || []).map((a) => a.id));
      } else {
        setForm({ ...EMPTY, service_type: settings.service_types?.[0] || "Sportivo" });
        setQuals([]);
        setDates([""]);
        setEnds([""]);
        setVehicleIds([]);
        setAmbIds([]);
      }
    }
  }, [open, service, template, settings.service_types]);

  useEffect(() => {
    if (open) api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleQual = (q) => setQuals((p) => (p.includes(q) ? p.filter((x) => x !== q) : [...p, q]));
  const toggleVehicle = (id) => setVehicleIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAmb = (id) => setAmbIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const ambVehicles = vehicles.filter((v) => v.is_ambulance);
  const ambMax = Number(form.required_ambulances) || 0;
  const addDate = () => { setDates((p) => [...p, ""]); setEnds((p) => [...p, ""]); };
  const removeDate = (i) => { setDates((p) => p.filter((_, idx) => idx !== i)); setEnds((p) => p.filter((_, idx) => idx !== i)); };
  const updateDate = (i, v) => setDates((p) => p.map((d, idx) => (idx === i ? v : d)));
  const updateEnd = (i, v) => setEnds((p) => p.map((d, idx) => (idx === i ? v : d)));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const pairs = dates.map((d, i) => ({ d, e: ends[i] })).filter((p) => p.d);
      const cleanDates = pairs.map((p) => p.d);
      const end_times = {};
      pairs.forEach((p) => { if (p.e) end_times[p.d] = p.e; });
      const payload = {
        ...form,
        date_time: cleanDates[0] || form.date_time,
        dates: cleanDates,
        end_times,
        required_ambulances: Number(form.required_ambulances),
        required_volunteers: Number(form.required_volunteers),
        required_medici: Number(form.required_medici) || 0,
        required_infermieri: Number(form.required_infermieri) || 0,
        required_qualifications: quals,
        required_vehicle_ids: vehicleIds,
        ambulance_ids: ambIds.slice(0, ambMax),
      };
      let data;
      if (isEdit) {
        ({ data } = await api.put(`/services/${service.id}`, payload));
        toast.success("Servizio aggiornato");
      } else {
        ({ data } = await api.post("/services", payload));
        toast.success("Servizio pubblicato", { description: "È ora visibile a tutti i volontari." });
      }
      onSaved(data, !isEdit);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Modifica servizio" : template ? "Duplica servizio" : "Pubblica un servizio"}</DialogTitle>
          {template && <p className="text-xs text-muted-foreground" data-testid="duplicate-hint">Copia di «{template.event_name}»: scegli la nuova data e controlla i dati. Iscritti e link WhatsApp non vengono copiati.</p>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="service-form">
          <div className="space-y-2">
            <Label htmlFor="event_name">Nome evento</Label>
            <Input id="event_name" data-testid="service-name-input" required value={form.event_name} onChange={(e) => set("event_name", e.target.value)} placeholder="Gara ciclistica Granfondo" />
          </div>
          <div className="space-y-2">
            <Label>Date e orari</Label>
            {dates.map((d, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[180px] space-y-1">
                  <span className="text-[11px] text-muted-foreground">Data e ora inizio</span>
                  <Input type="datetime-local" data-testid={`service-datetime-input-${i}`} required value={d} onChange={(e) => updateDate(i, e.target.value)} />
                </div>
                <div className="w-28 space-y-1">
                  <span className="text-[11px] text-muted-foreground">Ora fine</span>
                  <Input type="time" data-testid={`service-endtime-input-${i}`} value={ends[i] || ""} onChange={(e) => updateEnd(i, e.target.value)} />
                </div>
                {dates.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" data-testid={`remove-date-${i}`} onClick={() => removeDate(i)} className="shrink-0">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" data-testid="add-date" onClick={addDate} className="rounded-full">
              <Plus className="h-4 w-4 mr-1" /> Aggiungi data
            </Button>
          </div>
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
              <SelectTrigger data-testid="service-type-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>
                {[...new Set([...(settings.service_types || []), ...(form.service_type ? [form.service_type] : [])])].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="location">Luogo</Label>
            <Input id="location" data-testid="service-location-input" required value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Piazza Duomo, Orte" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amb">Ambulanze richieste</Label>
              <Input id="amb" type="number" min="0" data-testid="service-ambulances-input" value={form.required_ambulances} onChange={(e) => set("required_ambulances", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vol">Volontari richiesti</Label>
              <Input id="vol" type="number" min="0" data-testid="service-volunteers-input" value={form.required_volunteers} onChange={(e) => set("required_volunteers", e.target.value)} />
            </div>
          </div>
          {ambMax > 0 && (
            <div className="space-y-2" data-testid="ambulance-picker">
              <Label>Ambulanze coinvolte <span className="text-muted-foreground font-normal">({ambIds.length}/{ambMax}, con targa)</span></Label>
              {ambVehicles.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nessun mezzo segnato come ambulanza. Spunta "È un'ambulanza" in Admin → Autoparco.</p>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {ambVehicles.map((v) => {
                    const on = ambIds.includes(v.id);
                    return (
                      <label key={v.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted ${on ? "border-[#D32F2F]" : "border-border"}`}>
                        <Checkbox data-testid={`amb-vehicle-${v.id}`} checked={on} disabled={!on && ambIds.length >= ambMax} onCheckedChange={() => toggleAmb(v.id)} />
                        <span className="flex-1">{v.name}</span>
                        {v.plate && <span className="font-mono text-xs font-semibold">{v.plate}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              <AmbulanceConflicts ambIds={ambIds.slice(0, ambMax)} dates={dates} ends={ends} excludeId={service?.id} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="med">Medici richiesti</Label>
              <Input id="med" type="number" min="0" data-testid="service-medici-input" value={form.required_medici} onChange={(e) => set("required_medici", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inf">Infermieri richiesti</Label>
              <Input id="inf" type="number" min="0" data-testid="service-infermieri-input" value={form.required_infermieri} onChange={(e) => set("required_infermieri", e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Qualifiche richieste (volontari)</Label>
            <div className="grid grid-cols-2 gap-2">
              {volunteerQuals(settings.qualifications).map((q) => (
                <label key={q} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                  <Checkbox data-testid={`req-qual-${q}`} checked={quals.includes(q)} onCheckedChange={() => toggleQual(q)} />
                  <span>{q}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Altri mezzi richiesti (autoparco)</Label>
            {vehicles.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nessun mezzo in autoparco. Aggiungili dall'area Admin → Autoparco.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {vehicles.filter((v) => !v.is_ambulance).map((v) => (
                  <label key={v.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                    <Checkbox data-testid={`req-vehicle-${v.id}`} checked={vehicleIds.includes(v.id)} onCheckedChange={() => toggleVehicle(v.id)} />
                    <span className="flex-1">{v.name}{v.type ? ` · ${v.type}` : ""}{v.plate ? ` · ${v.plate}` : ""}</span>
                    {v.requires_autista && <Badge variant="secondary" className="text-[10px]">Autista</Badge>}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="wa">Link gruppo WhatsApp <span className="text-muted-foreground font-normal">(facoltativo, visibile solo agli iscritti)</span></Label>
            <Input id="wa" type="url" data-testid="service-whatsapp-input" value={form.whatsapp_link} onChange={(e) => set("whatsapp_link", e.target.value)} placeholder="https://chat.whatsapp.com/..." />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Note</Label>
            <Textarea id="notes" data-testid="service-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Ritrovo ore 7:30, divisa operativa…" />
          </div>
          <DuplicateWarning name={form.event_name} type={form.service_type} dates={dates} vehicleIds={[...ambIds.slice(0, ambMax), ...vehicleIds]} excludeId={service?.id} />
          <DialogFooter>
            <Button type="submit" data-testid="submit-service-button" disabled={loading} className="rounded-full font-semibold">
              {loading ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Pubblica servizio"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
